import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, Link, useNavigate } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { toast } from "react-toastify";
import {
  Book,
  Clock,
  Check,
  ArrowLeft,
  Archive,
  Star,
  StarOutline,
  DocumentText,
  Eye,
} from "iconsax-reactjs";
import {
  fetchCourseAssignments,
  submitAssignment,
  clearSubmissionResult,
} from "../../../redux/slices/progressSlice";
import { useFetchUser } from "../../../utils/useUserStore";

function CourseAssignments() {
  const { cohortId, enrollmentId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useFetchUser();

  const {
    courseAssignments,
    courseAssignmentsLoading,
    courseAssignmentsError,
    submitting,
    submissionResult,
    submitError,
  } = useSelector((state) => state.progress);

  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [submitForm, setSubmitForm] = useState({
    file: null,
    description: "",
  });

  useEffect(() => {
    if (cohortId || enrollmentId) {
      dispatch(fetchCourseAssignments({ cohortId, enrollmentId }));
    }
  }, [dispatch, cohortId, enrollmentId]);

  // Handle successful submission
  useEffect(() => {
    if (submissionResult) {
      toast.success(submissionResult.message || "Assignment submitted successfully!");
      setShowSubmitModal(false);
      setSubmitForm({ file: null, description: "" });
      dispatch(clearSubmissionResult());
      // Refresh the assignments
      dispatch(fetchCourseAssignments({ cohortId, enrollmentId }));
    }
  }, [submissionResult, dispatch, cohortId, enrollmentId]);

  // Handle submission error
  useEffect(() => {
    if (submitError) {
      toast.error(submitError.message || "Failed to submit assignment");
    }
  }, [submitError]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  // Get status badge
  const getStatusBadge = (assignment) => {
    if (assignment.is_completed) {
      return { class: "bg-success-subtle text-success", text: "Completed" };
    }
    if (assignment.is_submitted) {
      return { class: "bg-info-subtle text-info", text: "Submitted" };
    }
    if (assignment.is_pending) {
      return { class: "bg-warning-subtle text-warning", text: "Pending" };
    }
    return { class: "bg-secondary-subtle text-secondary", text: "Not Started" };
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSubmitForm({ ...submitForm, file });
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    await dispatch(
      submitAssignment({
        templateId: selectedAssignment?.template_id,
        cohortId: cohortId,
        courseId: course?.id,
        weekNumber: selectedAssignment?.week_number,
        file: submitForm.file,
        description: submitForm.description,
      })
    );
  };

  // Open submit modal
  const openSubmitModal = (assignment) => {
    setSelectedAssignment(assignment);
    setSubmitForm({ file: null, description: "" });
    setShowSubmitModal(true);
  };

  // View assignment details
  const viewAssignmentDetails = (templateId) => {
       const path = user?.account_type === 'organization_member' 
        ? `/organization/progress/assignment/${templateId}` 
        : `/progress/assignment/${templateId}`;
    navigate(path);
  };

  if (courseAssignmentsLoading) {
    return (
      <div className="dashboard-main-section">
        <div className="wrapper min-vh-100">
          <div className="header-area mb-4">
            <Skeleton width={200} height={30} />
            <Skeleton width={150} className="mt-2" />
          </div>
          <div className="row">
            {[1, 2, 3].map((i) => (
              <div className="col-md-6 mb-4" key={i}>
                <div className="card">
                  <div className="card-body">
                    <Skeleton height={120} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (courseAssignmentsError) {
    return (
      <div className="dashboard-main-section">
        <div className="wrapper min-vh-100">
          <div className="alert alert-danger" role="alert">
            <h4 className="alert-heading">Error</h4>
            <p>
              {courseAssignmentsError.message ||
                "Failed to load assignments. Please try again."}
            </p>
            <button
              className="btn btn-danger"
              onClick={() => dispatch(fetchCourseAssignments({ cohortId, enrollmentId }))}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { course, enrollment, summary, available_assignments, completed_assignments } =
    courseAssignments || {};

    // console.log("Available Assignments: ", available_assignments);
    // console.log("Completed Assignments: ", completed_assignments);

  return (
    <div className="dashboard-main-section">
      <div className="wrapper min-vh-100">
        {/* Header */}
         <button
            className="btn btn-button text-decoration-none mb-2 p-0"
            onClick={() => navigate(user?.account_type === 'organization_member' ? "/organization/progress" : "/progress")}
          >
            <ArrowLeft size={20} className="me-1" />
            Back to Progress
          </button>
        <div className="header-area mb-4">
         
          <h1 className="h4 fw-semibold mb-0">{course?.title || "Course Assignments"}</h1>
          <p className="text-muted">Manage and submit your assignments</p>
        </div>

        {/* Progress Summary */}
        {enrollment && (
          <div className="card mb-4">
            <div className="card-body">
              <div className="row align-items-center">
                <div className="col-md-8">
                  <h5 className="h6 fw-semibold mb-2">Your Progress</h5>
                  <div className="d-flex align-items-center">
                    <div className="progress flex-grow-1 me-3" style={{ height: "10px" }}>
                      <div
                        className="progress-bar bg-primary"
                        role="progressbar"
                        style={{ width: `${enrollment.progress || 0}%` }}
                        aria-valuenow={enrollment.progress || 0}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      />
                    </div>
                    <span className="fw-semibold">{enrollment.progress || 0}%</span>
                  </div>
                </div>
                <div className="col-md-4 text-md-end mt-3 mt-md-0">
                  <div className="text-muted small">
                    {summary?.completed || 0} of {summary?.total_expected || 0} assignments
                    completed
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <ul className="nav nav-tabs mb-4">
          <li className="nav-item">
            <button className="nav-link active">
              <Clock size={18} className="me-2" />
              Available ({available_assignments?.length || 0})
            </button>
          </li>
          <li className="nav-item">
            <button className="nav-link">
              <Check size={18} className="me-2" />
              Completed ({completed_assignments?.length || 0})
            </button>
          </li>
        </ul>

        {/* Available Assignments */}
        <div className="row">
          {available_assignments?.length === 0 ? (
            <div className="col-12">
              <div className="card">
                <div className="card-body text-center py-5">
                  <DocumentText size={48} className="text-muted mb-3" />
                  <h4>No Assignments Available</h4>
                  <p className="text-muted">
                    Assignments will appear here once they are released.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            available_assignments?.map((assignment) => {
              const status = getStatusBadge(assignment);
              return (
                <div className="col-md-6 mb-4" key={assignment.template_id}>
                  <div className="card h-100 shadow-sm">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <span className="badge bg-secondary mb-2">
                            Week {assignment.week_number}
                          </span>
                          <h5 className="h6 fw-semibold mb-1">{assignment.title}</h5>
                        </div>
                        <span className={`badge ${status.class}`}>{status.text}</span>
                      </div>

                      <p className="text-muted small mb-3">
                        {assignment.description?.substring(0, 100)}
                        {assignment.description?.length > 100 ? "..." : ""}
                      </p>

                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <span className="small text-muted">
                          <Archive size={14} className="me-1" />
                          Max Score: {assignment.max_score}
                        </span>
                        <span className="small text-muted">
                          Released: {formatDate(assignment.released_at)}
                        </span>
                      </div>

                      {assignment.submission && (
                        <div className="alert alert-info py-2 mb-3">
                          <div className="d-flex justify-content-between">
                            <span>
                              Status: <strong>{assignment.submission.status}</strong>
                            </span>
                            {assignment.submission.score && (
                              <span>
                                Score: <strong>{assignment.submission.score}/{assignment.submission.max_score}</strong>
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-primary btn-sm flex-grow-1"
                          onClick={() => viewAssignmentDetails(assignment.template_id)}
                        >
                          <Eye size={16} className="me-1" />
                          View Details
                        </button>
                        {assignment.can_submit && !assignment.is_submitted && (
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => openSubmitModal(assignment)}
                          >
                            <Archive size={16} className="me-1" />
                            Submit
                          </button>
                        )}
                        {assignment.is_submitted && !assignment.is_graded && (
                          <button className="btn btn-info btn-sm" disabled>
                            <Clock size={16} className="me-1" />
                            Awaiting Grading
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Completed Assignments Section */}
        {completed_assignments?.length > 0 && (
          <div className="mt-5">
            <h4 className="h5 fw-semibold mb-3">
              <Check size={20} className="me-2 text-success" />
              Completed Assignments
            </h4>
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Week</th>
                    <th>Title</th>
                    <th>Status</th>
                    <th>Score</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {completed_assignments.map((assignment) => (
                    <tr key={assignment.template_id}>
                      <td>Week {assignment.week_number}</td>
                      <td>{assignment.title}</td>
                      <td>
                        <span className="badge bg-success-subtle text-success">
                          {assignment.submission?.status || "Completed"}
                        </span>
                      </td>
                      <td>
                        {assignment.submission?.score !== null ? (
                          <span
                            className={`fw-semibold ${
                              (assignment.submission?.score / assignment.submission?.max_score) >= 0.7
                                ? "text-success"
                                : "text-warning"
                            }`}
                          >
                            {assignment.submission?.score}/{assignment.submission?.max_score}
                          </span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        <small className="text-muted">
                          {assignment.submission?.remarks || "-"}
                        </small>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Submit Modal */}
        {showSubmitModal && selectedAssignment && (
          <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Submit Assignment</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowSubmitModal(false)}
                  />
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="modal-body">
                    <div className="mb-3">
                      <h6>{selectedAssignment.title}</h6>
                      <p className="text-muted small">
                        Week {selectedAssignment.week_number} • Max Score:{" "}
                        {selectedAssignment.max_score}
                      </p>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Upload File (Optional)</label>
                      <input
                        type="file"
                        className="form-control"
                        onChange={handleFileChange}
                        accept=".pdf,.doc,.docx,.zip,.rar"
                      />
                      <small className="text-muted">
                        Accepted formats: PDF, DOC, DOCX, ZIP, RAR
                      </small>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Description (Optional)</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={submitForm.description}
                        onChange={(e) =>
                          setSubmitForm({ ...submitForm, description: e.target.value })
                        }
                        placeholder="Add any notes or description for your submission..."
                      />
                    </div>

                    {submitForm.file && (
                      <div className="alert alert-info py-2">
                        <Archive size={16} className="me-2" />
                        Selected: {submitForm.file.name} (
                        {(submitForm.file.size / 1024).toFixed(2)} KB)
                      </div>
                    )}
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowSubmitModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={submitting}
                    >
                      {submitting ? "Submitting..." : "Submit Assignment"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CourseAssignments;